import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { transformToSelectOptions } from '~/utils/common';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { GetCollectingResultReportsSearchConditionsResponse } from '~/types/delivery-agency.types';

export const fetchCollectingResultReportsSearchConditions = async () => {
  const { data } = await axiosInstance.get<GetCollectingResultReportsSearchConditionsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORTS_CODES
  );
  return data.data;
};

type UseGetCollectingResultReportsSearchConditionsQueryOptions = {
  enabled?: boolean;
  showGlobalErrorToast?: boolean;
};

export const useGetCollectingResultReportsSearchConditionsQuery = (
  options?: UseGetCollectingResultReportsSearchConditionsQueryOptions
) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.collectingResultReportsSearchConditions(),
    queryFn: fetchCollectingResultReportsSearchConditions,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 30,
    meta: { showGlobalErrorToast: options?.showGlobalErrorToast ?? true },
    select: (data) => ({
      searchKeywordTypes: transformToSelectOptions(data.searchKeywordTypes, 'code', 'description'),
      collectingShippingLabelStatuses: transformToSelectOptions(
        data.collectingShippingLabelStatuses,
        'code',
        'description'
      ),
      collectingFailureReasonCategories: transformToSelectOptions(
        data.collectingFailureReasonCategories,
        'code',
        'description'
      ),
    }),
  });
};

registerMockAPI<GetCollectingResultReportsSearchConditionsResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORTS_CODES,
  label: '회수 결과 리포트/검색조건',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          searchKeywordTypes: [
            { code: 'COLLECTING_REQUEST_CODE', description: '반품요청번호' },
            { code: 'COLLECTING_SHIPPING_LABEL_NUMBER', description: '회수 운송장번호' },
            { code: 'OUTBOUND_CODE', description: '출고번호' },
            { code: 'ORIGINAL_ORDER_CODE', description: '판매처 주문번호(원주문)' },
            { code: 'CONTACT_NAME', description: '원주문 수령자명' },
          ],
          collectingShippingLabelStatuses: [
            { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
            { code: 'COLLECT_DEPARTURE_READY', description: '회수 출발 준비' },
            { code: 'COLLECT_ON_THE_WAY', description: '회수 중' },
            { code: 'COLLECT_COMPLETED', description: '회수 완료' },
            { code: 'COLLECT_FAILED', description: '회수 실패' },
            { code: 'COLLECT_CANCELED', description: '회수 취소' },
          ],
          collectingFailureReasonCategories: [
            { code: 'PRODUCT_NOT_FOUND', description: '회수 장소에 상품 없음' },
            { code: 'ACCESS_DENIED', description: '출입문 잠김 / 진입 불가' },
            { code: 'BAD_WEATHER', description: '기상 악화' },
            { code: 'SCHEDULE_CHANGED', description: '회수일시 변경' },
          ],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: {} as GetCollectingResultReportsSearchConditionsResponse,
    },
  ],
});
